import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme, Theme } from '../styles/theme-manager';

interface ThemeToggleProps {
  className?: string;
  variant?: 'button' | 'dropdown' | 'segmented';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

/**
 * Theme toggle component with multiple UI variants
 * Provides accessible theme switching with smooth transitions
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  variant = 'button',
  size = 'md',
  showLabel = false,
}) => {
  const { theme, effectiveTheme, setTheme, toggleTheme } = useTheme();

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  if (variant === 'button') {
    return (
      <button
        onClick={toggleTheme}
        className={`
          ${sizeClasses[size]} 
          ${className}
          rounded-full 
          bg-theme-surface 
          border border-theme-border 
          hover:border-theme-border-hover 
          text-theme-primary 
          hover:text-theme-secondary
          transition-all duration-300 ease-in-out
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary focus-visible:ring-offset-2 focus-visible:ring-offset-theme-background
          flex items-center justify-center
          shadow-theme hover:shadow-lg
          backdrop-blur-sm
        `}
        aria-label={`Switch to ${effectiveTheme === 'dark' ? 'light' : 'dark'} mode`}
        title={`Switch to ${effectiveTheme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {effectiveTheme === 'dark' ? (
          <Sun size={iconSizes[size]} className="transition-transform duration-300 hover:rotate-12" />
        ) : (
          <Moon size={iconSizes[size]} className="transition-transform duration-300 hover:-rotate-12" />
        )}
      </button>
    );
  }

  if (variant === 'segmented') {
    const themes: { value: Theme; icon: React.ReactNode; label: string }[] = [
      { value: 'light', icon: <Sun size={16} />, label: 'Light' },
      { value: 'dark', icon: <Moon size={16} />, label: 'Dark' },
      { value: 'system', icon: <Monitor size={16} />, label: 'System' },
    ];

    return (
      <div className={`
        ${className}
        inline-flex rounded-full 
        bg-theme-surface 
        border border-theme-border 
        p-1 gap-1
        shadow-theme
        backdrop-blur-sm
      `}>
        {themes.map(({ value, icon, label }) => (
          <button
            key={value}
            onClick={() => setTheme(value)}
            className={`
              ${showLabel ? 'px-4 py-2' : 'w-10 h-10'}
              rounded-full 
              text-sm font-medium
              transition-all duration-300 ease-in-out
              flex items-center gap-2
              ${theme === value
                ? 'bg-theme-primary text-white shadow-md'
                : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-elevated'
              }
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary focus-visible:ring-offset-2 focus-visible:ring-offset-theme-background
            `}
            aria-label={`Switch to ${label} mode`}
            title={`Switch to ${label} mode`}
          >
            {icon}
            {showLabel && <span>{label}</span>}
          </button>
        ))}
      </div>
    );
  }

  // Dropdown variant
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={toggleTheme}
        className={`
          ${sizeClasses[size]}
          rounded-full 
          bg-theme-surface 
          border border-theme-border 
          hover:border-theme-border-hover 
          text-theme-primary 
          hover:text-theme-secondary
          transition-all duration-300 ease-in-out
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary focus-visible:ring-offset-2 focus-visible:ring-offset-theme-background
          flex items-center justify-center
          shadow-theme hover:shadow-lg
          backdrop-blur-sm
        `}
        aria-label={`Switch to ${effectiveTheme === 'dark' ? 'light' : 'dark'} mode`}
        title={`Switch to ${effectiveTheme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {effectiveTheme === 'dark' ? (
          <Sun size={iconSizes[size]} className="transition-transform duration-300 hover:rotate-12" />
        ) : (
          <Moon size={iconSizes[size]} className="transition-transform duration-300 hover:-rotate-12" />
        )}
      </button>
    </div>
  );
};

/**
 * Theme-aware icon component that adapts to current theme
 */
export const ThemeIcon: React.FC<{
  lightIcon: React.ReactNode;
  darkIcon: React.ReactNode;
  className?: string;
}> = ({ lightIcon, darkIcon, className = '' }) => {
  const { effectiveTheme } = useTheme();
  
  return (
    <div className={className}>
      {effectiveTheme === 'light' ? lightIcon : darkIcon}
    </div>
  );
};

/**
 * Theme-aware wrapper that applies theme transitions to its children
 */
export const ThemeProvider: React.FC<{
  children: React.ReactNode;
  defaultTheme?: Theme;
}> = ({ children }) => {
  // The theme is already applied via the useTheme hook
  // This component just ensures the theme is applied on mount
  
  return (
    <div className="theme-transition">
      {children}
    </div>
  );
};

// Default export for backward compatibility
export default ThemeToggle;
